import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, EMPTY } from 'rxjs';
import { TokenHolderService } from '@core/services/token-holder/token-holder-api';
import { OrgAndStockroomService } from '@core/services/org-and-stockroom/org-and-stockroom-api';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@core/services/toast-api';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AuthService } from '@auth0/auth0-angular';

export const BASE_API_URL = `${environment.BASE_API_URL}api/`;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const tokenHolderService = inject(TokenHolderService);
    const orgAndStockRoomService = inject(OrgAndStockroomService);
    const auth0 = inject(AuthService);
    const router = inject(Router);

    // ── 1. Local i18n JSON files — pass through with no headers ─────────────
    if (req.url.includes('/assets/i18n/') || req.url.includes('/i18n/')) {
        return next(req);
    }

    // ── 2. Translation API called at bootstrap before Auth0 token is stored ──
    // TranslateModule fires GET /api/languages/EN/labels immediately on load.
    // At that moment sessionStorage has no token yet, so we cannot add
    // Authtoken or run token-expiry validation (isTokenExpired(null) = true
    // which would trigger auth0.logout() incorrectly).
    // We attach only x-api-key — the minimum the backend needs for this
    // public/semi-public endpoint — and return early.
    if (req.url.includes('/api/languages/') && !tokenHolderService.getAuthToken()) {
        const translationReq = req.clone({
            headers: req.headers.set('x-api-key', environment.IM_API_KEY)
        });
        return next(translationReq);
    }

    // ── 3. Lazy-inject services that may not be ready at bootstrap ───────────
    let translateService: TranslateService | null = null;
    let toastService: ToastService | null = null;

    try {
        translateService = inject(TranslateService);
        toastService = inject(ToastService);
    } catch (error) {
        console.warn('[authInterceptor] Could not inject TranslateService/ToastService, using fallbacks');
    }

    // ── 4. Build the modified request for all authenticated endpoints ────────
    if (!req.url.endsWith('.html') && !req.url.endsWith('.js') && !req.url.endsWith('.css')) {
        const accessToken = tokenHolderService.getAuthToken();
        // console.log('[authInterceptor] Intercepting:', req.url, '| Token:', accessToken ? 'present' : 'missing');

        let imContext = orgAndStockRoomService.getStockroomAndOrg();
        tokenHolderService.removeConsumeProductsList();
        const isAdminConsole = sessionStorage.getItem('isAdminConsole') || 'No';

        let modifiedReq = req.clone({
            headers: req.headers.set('x-api-key', environment.IM_API_KEY)
        });

        if (accessToken) {
            modifiedReq = modifiedReq.clone({
                headers: modifiedReq.headers.set('Authtoken', accessToken),
            });
        }

        if (isAdminConsole === 'No') {
            if (modifiedReq.headers.has('im-context')) {
                modifiedReq = modifiedReq.clone({
                    headers: modifiedReq.headers.set(
                        'im-context',
                        modifiedReq.headers.get('im-context')!
                    ),
                });
            } else {
                modifiedReq = modifiedReq.clone({
                    headers: modifiedReq.headers.set('im-context', imContext!),
                });
            }
        } else {
            if (!modifiedReq.url.includes('activityLog')) {
                if (modifiedReq.headers.has('im-context')) {
                    modifiedReq = modifiedReq.clone({
                        headers: modifiedReq.headers.set(
                            'im-context',
                            modifiedReq.headers.get('im-context')!
                        )
                    });
                } else {
                    const isOrderTrackingContextEnabled = sessionStorage.getItem('isOrderTrackingContextEnabled') === 'true';
                    let stockroomAndOrg: any = null;

                    try {
                        stockroomAndOrg = JSON.parse(sessionStorage.getItem('stockroomAndOrg') || '{}');
                    } catch {
                        stockroomAndOrg = null;
                    }

                    const adminImContext: any = {
                        orgId: sessionStorage.getItem('admin_orgId') || stockroomAndOrg?.orgId || null
                    };

                    if (isOrderTrackingContextEnabled) {
                        adminImContext.stockroomId = stockroomAndOrg?.stockroomId ?? stockroomAndOrg?.stockRoomId ?? null;
                    }

                    modifiedReq = modifiedReq.clone({
                        headers: modifiedReq.headers.set('im-context', JSON.stringify(adminImContext))
                    });
                }
            }
        }

        req = modifiedReq;
    }

    if (req.url.includes('terms')) {
        req = req.clone({
            headers: req.headers.delete('User-Agent')
        });
    }

    // ── 5. Token validation — only reached for fully authenticated requests ──
    const isTokenInvalid = tokenHolderService.validateSessionToken();
    const expiry = tokenHolderService.getExpiresIn();
    const tokenRefresh = sessionStorage.getItem('tokenRefresh') === 'true';

    if ((expiry <= 0 || isTokenInvalid || isNaN(expiry))) {
        sessionStorage.clear();
        auth0.logout();
        return EMPTY;
    }

    // ── 6. Fire the request ──────────────────────────────────────────────────
    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 400 && translateService && toastService) {
                let errorMessage: any = '';
                try {
                    if (error?.error?.hasOwnProperty('label') && error?.error?.label && error?.error?.label != null) {
                        errorMessage = translateService.instant(error?.error?.label);
                    } else {
                        errorMessage = error?.error?.errorMessage?.includes('org.springframework.validation.BeanPropertyBindingResult') ?
                            translateService.instant('ime.please.enter.valid.input.to.search') : error?.error?.errorMessage;
                    }
                    if (errorMessage) {
                        // Use regex to remove the leading "400:" prefix.
                        // Previous substring/slice-based logic was not working in Stage 5.
                        const normalizedErrorMessage = errorMessage.replace(/^400\s*:?\s*/, '');
                        toastService.show(
                            'error',
                            normalizedErrorMessage,
                            '',
                            true,
                            5000
                        );
                    }
                } catch (err) {
                    console.error('[authInterceptor] Error showing translated message:', err);
                }
            }

            if (translateService && toastService &&
                error?.url !== environment.BASE_API_URL + 'stock/replenish' &&
                (error?.url === environment.BASE_API_URL + 'stock/replenish/price' ||
                    error?.url === environment.BASE_API_URL + 'stockroom/shipTo/address') &&
                (error?.status === 504 || error?.status === 0 || error?.statusText === 'Unknown Error')) {

                try {
                    const errorMsg = translateService.instant('ime.internal.server.error.please.try.again');
                    setTimeout(() => {
                        toastService!.show('error', errorMsg, '', false, 10000);
                    });
                } catch (err) {
                    console.error('[authInterceptor] Error showing timeout message:', err);
                }
            }

            return throwError(() => error);
        })
    );
};
